/**
 * Cookie Consent — единый источник истины (SSOT) для согласия на cookies.
 *
 * Читают/пишут это состояние: баннер `CookieConsent.tsx` (UI) и `api-logger.ts`
 * (гейтинг аналитики). Чтобы не дублировать формат/логику — вся работа с согласием
 * централизована здесь (GDPR/ePrivacy: без явного согласия трекинг недопустим).
 *
 * SSOT-хранилище — `localStorage['cookieConsent']`. `applyConsent` синхронизирует
 * решение пользователя с gtag (Consent Mode v2) и fbq: как ПРЕДОСТАВЛЕНИЕ, так и ОТЗЫВ.
 */

export interface CookiePreferences {
  /** Необходимые cookies — всегда true (нельзя отключить). */
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

export const COOKIE_CONSENT_KEY = "cookieConsent";
export const COOKIE_CONSENT_DATE_KEY = "cookieConsentDate";

/**
 * Событие изменения согласия (после сохранения/отзыва) — для реактивного UI.
 * Нужно, чтобы точка отзыва в футере появлялась ровно в момент принятия, а не
 * висела одновременно с баннером первичного согласия.
 */
export const CONSENT_CHANGED_EVENT = "cookie-consent-changed";

/**
 * Срок жизни согласия (R12): по истечении баннер показывается повторно.
 * 12 месяцев — консервативный ориентир регуляторов (CNIL/ICO: до ~6–13 мес).
 */
export const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000;

/**
 * Событие переоткрытия настроек cookie из любого места приложения
 * (например, кнопка «Настройки cookie» на странице приватности).
 * Слушает `CookieConsent.tsx`.
 */
export const OPEN_COOKIE_SETTINGS_EVENT = "open-cookie-settings";

export const DEFAULT_COOKIE_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

/**
 * Прочитать сохранённое согласие. Возвращает `null`, если согласие ещё не давалось
 * или запись повреждена (fail-closed — вызывающая сторона трактует это как «нет согласия»).
 * Поля нормализуются строгим сравнением с `true`, чтобы недоверенные данные из
 * localStorage не приводили к ложному «granted».
 */
export function readConsent(): CookiePreferences | null {
  try {
    if (typeof window === "undefined") return null;
    const raw = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookiePreferences>;
    return {
      necessary: true,
      analytics: parsed.analytics === true,
      marketing: parsed.marketing === true,
      preferences: parsed.preferences === true,
    };
  } catch {
    return null;
  }
}

/** Есть ли согласие именно на аналитику. Fail-closed при отсутствии/повреждении записи. */
export function hasAnalyticsConsent(): boolean {
  return readConsent()?.analytics === true;
}

/**
 * Global Privacy Control (CPRA §1798.135(b)): универсальный сигнал отказа от
 * «продажи»/«передачи» ПДн (cross-context behavioral advertising). Браузер или
 * расширение выставляет `navigator.globalPrivacyControl = true`. По CPRA бизнес ОБЯЗАН
 * честить сигнал как валидный opt-out от sale/sharing. У нас серверной продажи ПДн нет
 * (см. ADR-CCPA-opt-out-enforcement-and-gpc) — honor реализован декларативно на клиенте:
 * маркетинговая (рекламная) категория принудительно отключается.
 */
export function isGpcEnabled(): boolean {
  return (
    typeof navigator !== "undefined" && navigator.globalPrivacyControl === true
  );
}

/**
 * Эффективные предпочтения с учётом honor GPC. При активном GPC-сигнале маркетинговая
 * («sale/sharing») категория принудительно = false, даже если пользователь ранее принял
 * всё: обязательный opt-out «перевешивает» сохранённый выбор. Аналитика/функциональные
 * cookie остаются под явным согласием (GPC — сигнал против продажи/передачи, а не против
 * first-party аналитики). Идемпотентна и чиста (без side-effect).
 */
export function getEffectivePreferences(
  prefs: CookiePreferences,
): CookiePreferences {
  if (isGpcEnabled()) {
    return { ...prefs, marketing: false };
  }
  return prefs;
}

/**
 * Записано ли уже какое-либо решение по cookies. Используется, чтобы показывать
 * постоянную точку отзыва («Настройки cookie») ТОЛЬКО после первичного согласия —
 * пока показан баннер, второй точки входа быть не должно (устраняет визуальный
 * дубль на экране входа).
 */
export function hasStoredConsent(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(COOKIE_CONSENT_KEY) !== null;
  } catch {
    return false;
  }
}

/**
 * Подписка на изменения согласия: текущая вкладка (через `CONSENT_CHANGED_EVENT`)
 * и другие вкладки (через нативный `storage`). Возвращает функцию отписки.
 */
export function subscribeConsentChange(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener(CONSENT_CHANGED_EVENT, callback);
  window.addEventListener("storage", callback);
  return () => {
    window.removeEventListener(CONSENT_CHANGED_EVENT, callback);
    window.removeEventListener("storage", callback);
  };
}

/**
 * Истекло ли сохранённое согласие (R12 re-prompt по TTL).
 * Возвращает `true`, если запись согласия есть, но её дата старше `ttlMs`
 * (или дата отсутствует/некорректна — считаем устаревшей, безопасный дефолт —
 * переспросить). Если согласия нет вовсе — `false` (это не «истечение», а первый визит).
 */
export function isConsentExpired(ttlMs: number = CONSENT_TTL_MS): boolean {
  try {
    if (typeof window === "undefined") return false;
    if (window.localStorage.getItem(COOKIE_CONSENT_KEY) === null) return false;
    const dateRaw = window.localStorage.getItem(COOKIE_CONSENT_DATE_KEY);
    if (!dateRaw) return true;
    const ts = new Date(dateRaw).getTime();
    if (Number.isNaN(ts)) return true;
    return Date.now() - ts > ttlMs;
  } catch {
    return false;
  }
}

/** Сохранить согласие в SSOT-хранилище вместе с датой (для R12 re-prompt по TTL). */
export function saveConsent(prefs: CookiePreferences): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(prefs));
  window.localStorage.setItem(
    COOKIE_CONSENT_DATE_KEY,
    new Date().toISOString(),
  );
  // Реактивно уведомляем UI (футер): решение принято → показать точку отзыва.
  window.dispatchEvent(new Event(CONSENT_CHANGED_EVENT));
}

/**
 * Best-effort удаление GA-cookies при отзыве аналитики. Каноничный механизм —
 * gtag Consent Mode (`denied`), но GDPR требует и физического удаления ранее
 * установленных трекинг-cookies. Чистит на текущем и родительском домене.
 */
function clearAnalyticsCookies(): void {
  if (typeof document === "undefined" || typeof window === "undefined") return;
  try {
    const gaPattern = /^(_ga|_gid|_gat|_ga_[A-Z0-9]+|_gac_)/;
    const hostname = window.location.hostname;
    const domains = new Set<string>(["", hostname, `.${hostname}`]);
    const parts = hostname.split(".");
    if (parts.length > 2) {
      domains.add(`.${parts.slice(-2).join(".")}`);
    }
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0]?.trim();
      if (!name || !gaPattern.test(name)) return;
      domains.forEach((domain) => {
        document.cookie =
          `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/` +
          (domain ? `; domain=${domain}` : "");
      });
    });
  } catch {
    // очистка cookie не должна ломать поток согласия
  }
}

/**
 * Синхронизировать решение пользователя с внешними трекерами (gtag Consent Mode v2, fbq).
 * Идемпотентно: вызывается при загрузке (восстановление) и при каждом сохранении/отзыве.
 * КРИТИЧНО: обрабатывает и предоставление, и ОТЗЫВ — иначе выключение категории не
 * останавливало бы уже инициализированный трекинг.
 */
export function applyConsent(prefs: CookiePreferences): void {
  if (typeof window === "undefined") return;

  // honor GPC: при активном сигнале маркетинг принудительно denied вне зависимости
  // от сохранённого выбора (обязательный CPRA opt-out «перевешивает» согласие).
  const effective = getEffectivePreferences(prefs);

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: effective.analytics ? "granted" : "denied",
      ad_storage: effective.marketing ? "granted" : "denied",
      ad_user_data: effective.marketing ? "granted" : "denied",
      ad_personalization: effective.marketing ? "granted" : "denied",
      personalization_storage: effective.preferences ? "granted" : "denied",
      functionality_storage: "granted",
      security_storage: "granted",
    });
  }

  if (typeof window.fbq === "function") {
    window.fbq("consent", effective.marketing ? "grant" : "revoke");
  }

  if (!effective.analytics) {
    clearAnalyticsCookies();
  }
}

/**
 * Переоткрыть баннер настроек cookie из любого места приложения.
 * `CookieConsent.tsx` слушает событие и открывает диалог в режиме настроек.
 */
export function openCookieSettings(): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new Event(OPEN_COOKIE_SETTINGS_EVENT));
}
